import * as React from 'react';
// Import services.
import {
	AddressService,
} from '@app/services';
// Import hooks.
import {
	useAddressVerification,
	UseAddressVerificationResults,
} from '@app/hooks/view';
import {
	UseInputAddressState,
	UseInputFormResult,
	useInputLienholderPosition,
	UseInputLienholderPositionResults,
	useInputMortgageeEmail,
	UseInputMortgageeEmailResults,
	useInputText,
	UseInputTextResults,
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormMortgageeBaseProps,
} from '@app/props';
// Import api models.
import {
	Address,
	MortgageeRequest,
} from '@app/models/api';

interface UseFormMortgageeBaseParameters<TProps extends FormMortgageeBaseProps> {
	id: string;
	props: TProps;
}

interface UseFormMortgageeBaseResults {
	areAllInputsDisabled: boolean
	showMortgageeNameInput: boolean;
	showMortgageeAddressInput: boolean;
	showIncludeInterestsInput: boolean;
	showIncludeSuccessorOrAssignsInput: boolean;
	showLienholderPositionInput: boolean;
	showLoanNumberInput: boolean;
	showMortgageeEmailInput: boolean;
	showSendPaymentReceiptToMortgageeInput: boolean;
	showMortgageeBilledInput: boolean;
	showMortgageeRequiredInput: boolean;
	isSendPaymentReceiptToMortgageeRequired: boolean;
	mortgageeNameInput: UseInputTextResults;
	addressVerification: UseAddressVerificationResults;
	includeInterestsInput: UseInputYesNoResults;
	includeSuccessorOrAssignsInput: UseInputYesNoResults;
	lienholderPositionInput: UseInputLienholderPositionResults;
	loanNumberInput: UseInputTextResults;
	mortgageeEmailInput: UseInputMortgageeEmailResults;
	sendPaymentReceiptToMortgageeInput: UseInputYesNoResults;
	isMortgageeBilledInput: UseInputYesNoResults;
	isMortgageeRequiredInput: UseInputYesNoResults;
	submitForm: () => void;
	checkFormValidation: (focusToElement: boolean) => boolean;
	doesFormHaveChanges: () => boolean;
}

const useFormMortgageeBase = <TProps extends FormMortgageeBaseProps>(params: UseFormMortgageeBaseParameters<TProps>): UseFormMortgageeBaseResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			proceedWithFormSubmission();		
		}
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return mortgageeNameInput.doesInputHaveChanges
			|| addressVerification.addressLocationInput.doesInputHaveChanges
			|| includeInterestsInput.doesInputHaveChanges
			|| includeSuccessorOrAssignsInput.doesInputHaveChanges
			|| lienholderPositionInput.doesInputHaveChanges
			|| loanNumberInput.doesInputHaveChanges
			|| mortgageeEmailInput.doesInputHaveChanges
			|| sendPaymentReceiptToMortgageeInput.doesInputHaveChanges
			|| isMortgageeBilledInput.doesInputHaveChanges
			|| isMortgageeRequiredInput.doesInputHaveChanges;
	}

	/**
	 * Method to check form validation. 
	 * @param focusToElement
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<string>(mortgageeNameInput, focusToElement)
			&& checkInputValidation<UseInputAddressState>(addressVerification.addressLocationInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(includeInterestsInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(includeSuccessorOrAssignsInput, focusToElement)
			&& checkInputValidation<number | undefined>(lienholderPositionInput, focusToElement)
			&& checkInputValidation<string>(loanNumberInput, focusToElement)
			&& checkInputValidation<string>(mortgageeEmailInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(sendPaymentReceiptToMortgageeInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(isMortgageeBilledInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(isMortgageeRequiredInput, focusToElement);
	}

	// #endregion exported methods


	// #region private methods

	const proceedWithFormSubmission = (): void => {
		if (addressVerification.addressLocationInput.doesInputHaveChanges) {
			// If any changes were made to the mailing address, proceed to verify the address.
			submitFormWithAddressVerification();
		}
		else {
			// If no changes have been made to the address, then proceed to save the insured information.
			params.props.onSaveMortgagee(createMortgageeWithMailingAddress());
		}
	}

	/**
	 * Starts the submission process without validation for the address. 
	 **/
	const submitFormWithAddressVerification = (): void => {
		// Proceed forward if form validates successfully.
		try {
			if (addressVerification.addressLocationInput.value.address !== undefined) {
				// If the input address components are already provided, then we need to verify them as address components.
				addressVerification
					.verifyAddressAsComponentsAsync(addressVerification.addressLocationInput.value.address);
			}
			else if (!String.isNullOrWhiteSpace(addressVerification.addressLocationInput.value.addressSingleLine)) {
				// If only a single line address input is provided, then we need to verify that first.
				addressVerification
					.verifyAddressAsSingleLineAsync(addressVerification.addressLocationInput.value.addressSingleLine);
			}
		}
		catch (error: any) {
			console.log(error);
		}
	}

	/**
	 * Saves the insured info after finishing the address verification process.
	 * @param address
	 */
	const finishConfirmAddressVerification = (address: Address): void => {
		// Save the mortgagee after finishing the address verification process.
		params.props.onSaveMortgagee(createMortgageeWithProvidedMailingAddress(address));
	}

	/**
	 * Creates an mortgagee record by attaching the address input.
	 **/
	const createMortgageeWithMailingAddress = (): MortgageeRequest => {
		let mortgagee: MortgageeRequest = createMortgagee();
		mortgagee.address = addressVerification.addressLocationInput.value.address ?? null;
		return mortgagee;
	}

	/**
	 * Creates an mortgagee record by attaching the provided address input.
	 * @param mailingAddress
	 */
	const createMortgageeWithProvidedMailingAddress = (mailingAddress: Address): MortgageeRequest => {
		let mortgagee: MortgageeRequest = createMortgagee();
		mortgagee.address = mailingAddress;
		return mortgagee;
	}

	/**
	 * Creates an insured info record by collecting all user inputs.
	 **/
	const createMortgagee = (): MortgageeRequest => {
		let mortgagee: MortgageeRequest = new MortgageeRequest();
		mortgagee.name = mortgageeNameInput.value;
		mortgagee.includeInterests = includeInterestsInput.value ?? null;
		mortgagee.includeSuccessorOrAssigns = includeSuccessorOrAssignsInput.value ?? null;
		mortgagee.positionNumber = lienholderPositionInput.value ?? null;
		mortgagee.loanNumber = loanNumberInput.value ?? null;
		mortgagee.email = mortgageeEmailInput.value ?? null;
		mortgagee.sendPaymentReceiptToMortgagee = sendPaymentReceiptToMortgageeInput.value ?? null;
		mortgagee.isMortgageeBilled = isMortgageeBilledInput.value ?? null;
		mortgagee.isMortgageeRequired = isMortgageeRequiredInput.value ?? null;

		return mortgagee;
	}

	/**
	 * Validates an individual input.
	 * @param input
	 * @param focusToElement
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		mortgageeNameInput.changeShowValidationFlag(true);
		addressVerification.addressLocationInput.changeShowValidationFlag(true);
		includeInterestsInput.changeShowValidationFlag(true);
		includeSuccessorOrAssignsInput.changeShowValidationFlag(true);
		lienholderPositionInput.changeShowValidationFlag(true);
		loanNumberInput.changeShowValidationFlag(true);
		mortgageeEmailInput.changeShowValidationFlag(true);
		sendPaymentReceiptToMortgageeInput.changeShowValidationFlag(true);
		isMortgageeBilledInput.changeShowValidationFlag(true);
		isMortgageeRequiredInput.changeShowValidationFlag(true);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for mortgagee name. 
	 **/
	const generateIdForMortgageeName = (): string => {
		return `${generateIdForForm()}-mortgageeName`;
	}

	/**
	 * Generate id for the mailing address. 
	 **/
	const generateIdForMailingAddress = (): string => {
		return `${generateIdForForm()}-mailingAddress`;
	}

	/**
	 * Generate id for include interests. 
	 **/
	const generateIdForIncludeInterests = (): string => {
		return `${generateIdForForm()}-includeInterests`;
	}

	/**
	 * Generate id for include successor or assigns. 
	 **/
	const generateIdForIncludeSuccessorOrAssigns = (): string => {
		return `${generateIdForForm()}-includeSuccessorOrAssigns`;
	}

	/**
	 * Generate id for lienholder position. 
	 **/
	const generateIdForLienholderPosition = (): string => {
		return `${generateIdForForm()}-lienholderPosition`;
	}

	/**
	 * Generate id for loan number. 
	 **/
	const generateIdForLoanNumber = (): string => {
		return `${generateIdForForm()}-loanNumber`;
	}

	/**
	 * Generate id for mortgagee email. 
	 **/
	const generateIdForMortgageeEmail = (): string => {
		return `${generateIdForForm()}-mortgageeEmail`;
	}

	/**
	 * Generate id for send payment receipt to mortgagee. 
	 **/
	const generateIdForSendPaymentReceiptToMortgagee = (): string => {
		return `${generateIdForForm()}-sendPaymentReceiptToMortgagee`;
	}

	/**
	 * Generate id for is mortgagee billed. 
	 **/
	const generateIdForIsMortgageeBilled = (): string => {
		return `${generateIdForForm()}-isMortgageeBilled`;
	}

	/**
	 * Generate id for is mortgagee required. 
	 **/
	const generateIdForIsMortgageeRequired = (): string => {
		return `${generateIdForForm()}-isMortgageeRequired`;
	}

	//#endregion id generators

	// #region input initializers

	/**
	 * Sets up the initial value for the 'mailing address' input.
	 **/
	const setupInitialMailingAddress = (): Address | undefined => {
		let currentMailingAddress: Address | undefined = params.props.mortgagee?.address ?? undefined;
		return currentMailingAddress !== undefined
			&& !AddressService.getInstance().isAddressEmpty(currentMailingAddress)
			? { ...currentMailingAddress }
			: undefined;
	}

	// #endregion input initializers

	// #endregion private methods


	// #region variables

	const allowEdits: boolean = React.useMemo(() => params.props.allowEdits ?? true, [params.props.allowEdits])

	// #region Input settings visibility.

	const showMortgageeNameInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.mortgageeNameInput !== 'hidden',
		[params.props.inputSettingsProps?.mortgageeNameInput]
	);
	const showMortgageeAddressInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.mortgageeAddressInput !== 'hidden',
		[params.props.inputSettingsProps?.mortgageeAddressInput]
	);
	const showIncludeInterestsInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.includeInterestsInput !== 'hidden',
		[params.props.inputSettingsProps?.includeInterestsInput]
	);
	const showIncludeSuccessorOrAssignsInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.includeSuccessorOrAssignsInput !== 'hidden',
		[params.props.inputSettingsProps?.includeSuccessorOrAssignsInput]
	);
	const showLienholderPositionInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.lienholderPositionInput !== 'hidden',
		[params.props.inputSettingsProps?.lienholderPositionInput]
	);
	const showLoanNumberInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.loanNumberInput !== 'hidden',
		[params.props.inputSettingsProps?.loanNumberInput]
	);
	const showMortgageeEmailInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.mortgageeEmailInput !== 'hidden',
		[params.props.inputSettingsProps?.mortgageeEmailInput]
	);
	const showSendPaymentReceiptToMortgageeInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.sendPaymentReceiptToMortgageeInput !== 'hidden',
		[params.props.inputSettingsProps?.sendPaymentReceiptToMortgageeInput]
	);
	const showMortgageeBilledInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.mortgageeBilledInput !== 'hidden',
		[params.props.inputSettingsProps?.mortgageeBilledInput]
	);
	const showMortgageeRequiredInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.mortgageeRequiredInput !== 'hidden',
		[params.props.inputSettingsProps?.mortgageeRequiredInput]
	);


	// #endregion Input settings visibility.

	// Input settings.
	const isMortgageeNameInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showMortgageeNameInput
		|| params.props.inputSettingsProps?.mortgageeNameInput === 'disabled',
		[
			allowEdits,
			showMortgageeNameInput,
			params.props.inputSettingsProps?.mortgageeNameInput,
		]
	);
	const isMortgageeAddressInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showMortgageeAddressInput
		|| params.props.inputSettingsProps?.mortgageeAddressInput === 'disabled',
		[
			allowEdits,
			showMortgageeAddressInput,
			params.props.inputSettingsProps?.mortgageeAddressInput
		]
	);
	const isIncludeInterestsInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showIncludeInterestsInput
		|| params.props.inputSettingsProps?.includeInterestsInput === 'disabled',
		[
			allowEdits,
			showIncludeInterestsInput,
			params.props.inputSettingsProps?.includeInterestsInput
		]
	);
	const isIncludeSuccessorOrAssignsInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showIncludeSuccessorOrAssignsInput
		|| params.props.inputSettingsProps?.includeSuccessorOrAssignsInput === 'disabled',
		[
			allowEdits,
			showIncludeSuccessorOrAssignsInput,
			params.props.inputSettingsProps?.includeSuccessorOrAssignsInput
		]
	);
	const isLienholderPositionInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showLienholderPositionInput
		|| params.props.inputSettingsProps?.lienholderPositionInput === 'disabled',
		[
			allowEdits,
			showLienholderPositionInput,
			params.props.inputSettingsProps?.lienholderPositionInput
		]
	);
	const isLoanNumberInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showLoanNumberInput
		|| params.props.inputSettingsProps?.loanNumberInput === 'disabled',
		[
			allowEdits,
			showLoanNumberInput,
			params.props.inputSettingsProps?.loanNumberInput
		]
	);
	const isMortgageeEmailInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showMortgageeEmailInput
		|| params.props.inputSettingsProps?.mortgageeEmailInput === 'disabled',
		[
			allowEdits,
			showMortgageeEmailInput,
			params.props.inputSettingsProps?.mortgageeEmailInput
		]
	);
	const isSendPaymentReceiptToMortgageeInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showSendPaymentReceiptToMortgageeInput
		|| params.props.inputSettingsProps?.sendPaymentReceiptToMortgageeInput === 'disabled',
		[
			allowEdits,
			showSendPaymentReceiptToMortgageeInput,
			params.props.inputSettingsProps?.sendPaymentReceiptToMortgageeInput
		]
	);
	const isMortgageeBilledInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showMortgageeBilledInput
		|| params.props.inputSettingsProps?.mortgageeBilledInput === 'disabled',
		[
			allowEdits,
			showMortgageeBilledInput,
			params.props.inputSettingsProps?.mortgageeBilledInput
		]
	);
	const isMortgageeRequiredInputDisabled: boolean = React.useMemo(() =>
		!allowEdits
		|| !showMortgageeRequiredInput
		|| params.props.inputSettingsProps?.mortgageeRequiredInput === 'disabled',
		[
			allowEdits,
			showMortgageeRequiredInput,
			params.props.inputSettingsProps?.mortgageeRequiredInput
		]
	);

	let initialMailingAddress: Address | undefined = setupInitialMailingAddress();

	// Instantiate the 'mortgagee name' input hook.
	const mortgageeNameInput: UseInputTextResults = useInputText({
		id: generateIdForMortgageeName(),
		label: 'Mortgagee Name',
		required: true,
		disabled: isMortgageeNameInputDisabled,
		value: params.props.mortgagee?.name ?? String.empty(),
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'address verification' hook.
	let addressVerification: UseAddressVerificationResults = useAddressVerification({
		addressLocationInputParameters: {
			addressInputParameters: {
				id: generateIdForMailingAddress(),
				label: 'Enter Address, City, State, Zip',
				required: true,
				disabled: isMortgageeAddressInputDisabled,
				showValidation: params.props.showValidationErrorsOnMount,
				initialInput: {
					address: initialMailingAddress,
					addressSingleLine: String.empty(),
				},
				possibleStates: params.props.possibleStates,
				onGetAddressSuggestions: params.props.addressProps.getAddressSuggestions,
			},
		},
		finishConfirmAddressVerification: finishConfirmAddressVerification,
		possibleStates: params.props.possibleStates,
		ignorePossibleStateRiskAllowed: true,
		initialInput: undefined,
		verifySingleLineAddress: params.props.addressProps.verifySingleLineAddress,
		verifyAddress: params.props.addressProps.verifyAddress,
	});

	// Instantiate the 'include interests' input.
	const includeInterestsInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIncludeInterests(),
		label: 'Include "As Their Interests May Appear"?',
		required: true,
		disabled: isIncludeInterestsInputDisabled,
		isYesChecked: params.props.mortgagee?.includeInterests ?? undefined,
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'include successor or assigns' input.
	const includeSuccessorOrAssignsInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIncludeSuccessorOrAssigns(),
		label: 'Include "Its Successors and/or Assigns"?',
		required: true,
		disabled: isIncludeSuccessorOrAssignsInputDisabled,
		isYesChecked: params.props.mortgagee?.includeSuccessorOrAssigns ?? undefined,
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'lienholder position' input hook.
	const lienholderPositionInput: UseInputLienholderPositionResults = useInputLienholderPosition({
		id: generateIdForLienholderPosition(),
		required: true,
		disabled: isLienholderPositionInputDisabled,
		value: params.props.mortgagee?.positionNumber ?? undefined,
		showValidation: params.props.showValidationErrorsOnMount,
		lienholderPositionsInUse: params.props.mortgageeLienholderPositionsInUse,
	});

	// Instantiate the 'loan number' input hook.
	const loanNumberInput: UseInputTextResults = useInputText({
		id: generateIdForLoanNumber(),
		label: 'Loan Number',
		required: true,
		disabled: isLoanNumberInputDisabled,
		value: params.props.mortgagee?.loanNumber ?? String.empty(),
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'mortgagee email' input hook.
	const mortgageeEmailInput: UseInputMortgageeEmailResults = useInputMortgageeEmail({
		id: generateIdForMortgageeEmail(),
		required: false,
		disabled: isMortgageeEmailInputDisabled,
		value: params.props.mortgagee?.email ?? String.empty(),
		showValidation: params.props.showValidationErrorsOnMount,
		mortgageeEmailsInUse: params.props.mortgageeEmailsInUse,
	});

	const isSendPaymentReceiptToMortgageeRequired: boolean = React.useMemo(() => {
		return !String.isNullOrWhiteSpace(mortgageeEmailInput.value);
	}, [mortgageeEmailInput.value]);

	// Instantiate the 'send payment receipt to mortgagee' input.
	const sendPaymentReceiptToMortgageeInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForSendPaymentReceiptToMortgagee(),
		label: 'Would you like a copy of the payment receipt sent to the mortgagee email address?',
		required: true,
		disabled: !isSendPaymentReceiptToMortgageeRequired || isSendPaymentReceiptToMortgageeInputDisabled,
		isYesChecked: params.props.mortgagee?.sendPaymentReceiptToMortgagee ?? false,
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'is mortgagee billed' input.
	const isMortgageeBilledInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIsMortgageeBilled(),
		label: 'Bill mortgage company on renewal?',
		required: true,
		disabled: isMortgageeBilledInputDisabled,
		isYesChecked: params.props.mortgagee?.isMortgageeBilled ?? undefined,
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Instantiate the 'is mortgagee required' input.
	const isMortgageeRequiredInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIsMortgageeRequired(),
		label: 'Is this policy being required by the mortgagee?',
		required: true,
		disabled: isMortgageeRequiredInputDisabled,
		isYesChecked: params.props.mortgagee?.isMortgageeRequired ?? undefined,
		showValidation: params.props.showValidationErrorsOnMount,
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return mortgageeNameInput.isDisabled
			&& addressVerification.addressLocationInput.isDisabled
			&& includeInterestsInput.isDisabled
			&& includeSuccessorOrAssignsInput.isDisabled
			&& lienholderPositionInput.isDisabled
			&& loanNumberInput.isDisabled
			&& mortgageeEmailInput.isDisabled
			&& sendPaymentReceiptToMortgageeInput.isDisabled
			&& isMortgageeBilledInput.isDisabled
			&& isMortgageeRequiredInput.isDisabled;
	}, [
		mortgageeNameInput.isDisabled,
		addressVerification.addressLocationInput.isDisabled,
		includeInterestsInput.isDisabled,
		includeSuccessorOrAssignsInput.isDisabled,
		lienholderPositionInput.isDisabled,
		loanNumberInput.isDisabled,
		mortgageeEmailInput.isDisabled,
		sendPaymentReceiptToMortgageeInput.isDisabled,
		isMortgageeBilledInput.isDisabled,
		isMortgageeRequiredInput.isDisabled,
	]);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.props.showValidationErrorsOnMount
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			mortgageeNameInput.focusToInput();
		}
	}, []);

	// Called when the mortgagee email is updated.
	React.useEffect(() => {
		if (String.isNullOrWhiteSpace(mortgageeEmailInput.value)) {
			// Set the payment receipt input to false if email is removed.
			sendPaymentReceiptToMortgageeInput.setInput(false);
		}
	}, [mortgageeEmailInput.value]);

	// #endregion life-cycle methods.


	return {
		areAllInputsDisabled,
		showMortgageeNameInput,
		showMortgageeAddressInput,
		showIncludeInterestsInput,
		showIncludeSuccessorOrAssignsInput,
		showLienholderPositionInput,
		showLoanNumberInput,
		showMortgageeEmailInput,
		showSendPaymentReceiptToMortgageeInput,
		showMortgageeBilledInput,
		showMortgageeRequiredInput,
		isSendPaymentReceiptToMortgageeRequired,
		mortgageeNameInput,
		addressVerification,
		includeInterestsInput,
		includeSuccessorOrAssignsInput,
		lienholderPositionInput,
		loanNumberInput,
		mortgageeEmailInput,
		sendPaymentReceiptToMortgageeInput,
		isMortgageeBilledInput,
		isMortgageeRequiredInput,
		submitForm,
		checkFormValidation,
		doesFormHaveChanges,
	};
}

// Export parameters.
export {
	UseFormMortgageeBaseParameters,
}

// Export results.
export {
	UseFormMortgageeBaseResults,
}

export default useFormMortgageeBase;